import { GuideNavigation } from '../GuideNavigation/GuideNavigation.component';
import { GuideEpisodeContext } from 'pages/les-guides/[slug]';
import { ContactSubject } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ADVERT_FORMAT_ID, ADVERT_PAGE_ID } from '@adverts/business/Adverts.contants';
import { useAdvert } from '@adverts/business/useAdvert';
import { GenericAd } from '@adverts/components/GenericAd.component';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { BasicLink } from '@components/buttons/BasicLink/BasicLink.component';
import { ClippedImage } from '@components/ClippedImage/ClippedImage.component';
import { ContentBody } from '@components/content/ContentBody/ContentBody.component';
import { ContentFooter } from '@components/content/ContentFooter/ContentFooter.component';
import { ContentLayout, ContentLayoutLoaderOrError } from '@components/content/ContentLayout/ContentLayout.component';
import { ContentSubtitle } from '@components/content/ContentSubtitle/ContentSubtitle.component';
import { LastContent } from '@components/content/LastContent/LastContent.component';
import { ProTag, ProTagVariant } from '@components/ProTag/ProTag.component';
import { Text } from '@components/Text/Text.component';
import { getEpisodeInfos } from '@guides/business/Guides.utils';
import { HorizontalSeparator } from '@layout/components/HorizontalSeparator/HorizontalSeparator.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { SearchContentType } from '@search/business/search.types';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { userSelectors } from '@user/business/User.store';
import { generateLightCardFromGuide } from '@utils/card';
import { formatDateCompact, formatDateToIso } from '@utils/date';
import { ClippedImageStyle } from '@utils/images';
import { isServer } from '@utils/server';
import { getContactHref, urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';

export const GuideEpisodeLayout: FC<ComponentPropsWithRef<'div'>> = ({ className, ...rest }) => {
  const { t } = useTranslation(['guides']);
  const {
    guideEpisode: { data: guideEpisode, isValidating: isGuideEpisodeLoading, retryCallback },
    lastGuides: { data: lastGuides, ...restLastGuides },
    isPreview: { data: isPreview },
  } = useContext(GuideEpisodeContext);
  const userIsPro = useSelector(userSelectors.isPro);
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const lastGuidesCards = useMemo(() => {
    const cards = lastGuides ? lastGuides.map(generateLightCardFromGuide) : [];
    return (
      <LastContent
        cards={cards}
        contentType={SearchContentType.Guide}
        seeAllLinkI18nKey="guides.last.see-all"
        titleI18nKey="guides.last.title"
        {...restLastGuides}
      />
    );
  }, [lastGuides, restLastGuides]);

  useAdvert({
    pageId: ADVERT_PAGE_ID.GuideDetail,
  });

  const tagProps: PlcTagProps = useMemo(
    () => ({
      isDataLoading: isGuideEpisodeLoading,
      level2: ATLevel2.Guides,
      tagPageNameI18nArray: ['guides.tag.title', guideEpisode ? { code: guideEpisode.slug, notI18n: true } : undefined],
    }),
    [guideEpisode, isGuideEpisodeLoading]
  );

  const rootClassName = classnames('episode', className);

  useEffect(() => {
    if (isServer() || !guideEpisode) {
      return () => undefined;
    }
    // Move the overflow from page-content to content-layout to allow GuideNavigation to be sticky
    const pageContent = document.getElementsByClassName('page-content')[0];
    const contentLayout = document.getElementsByClassName('content-layout')[0];

    setTimeout(() => {
      pageContent?.setAttribute('style', 'overflow: initial');
      contentLayout?.setAttribute('style', 'overflow-x: hidden');
    }, 350);

    return () => {
      pageContent?.setAttribute('style', '');
      contentLayout?.setAttribute('style', 'overflow-x:initial');
    };
  }, [guideEpisode]);

  if (!guideEpisode) {
    return (
      <ContentLayoutLoaderOrError
        className={rootClassName}
        isLoading={isGuideEpisodeLoading}
        retryCallback={retryCallback}
        withIllu={true}
        {...rest}
      />
    );
  }

  const {
    isPro: articleIsPro,
    metaTitle,
    metaDescription,
    title,
    body,
    interestCodes = [],
    activityCodes = [],
    featuredMedia,
    featuredMediaMobile,
    subtitle,
    publishDate,
    signature,
    guideInfo: { name: guideTitle, episodeList },
    canonicalLink,
    slug,
  } = guideEpisode;

  const steps: BreadCrumbStep[] = [
    {
      label: 'common.newspaper-short',
      linkProps: { as: urlEnum.journal, href: urlEnum.journal },
    },
    {
      label: 'common.guides',
      linkProps: { as: urlEnum.guides, href: urlEnum.guides },
    },
    {
      label: guideTitle,
      notI18n: true,
    },
  ];

  const bodyIsPartial = articleIsPro && !userIsPro && !isPreview;
  const { episodesCount, currentEpisode, isSummary } = getEpisodeInfos(episodeList);
  const infos = isSummary
    ? { i18nKey: 'guides.summary' }
    : { i18nKey: 'guides.current-episode-with-total', i18nOptions: { count: episodesCount, current: currentEpisode } };
  const titleVariant = isMobile ? 'h4' : 'h3';
  const breakpoint = 'm';
  const contactMessage = t('guides.contact-message', { episodeTitle: title, guideTitle });

  return (
    <>
      <ContentLayout
        articleTitle={guideTitle}
        articleUrl={`${urlEnum.guides}/${slug}`}
        breadcrumbSteps={steps}
        className={rootClassName}
        description={subtitle}
        gridBottomComponent={
          <>
            <Text className="plc-mb-m" i18nKey="guides.suggestion" tag="h2" variant="h3" />
            <BasicLink
              href={getContactHref(ContactSubject.GuideReaction, contactMessage)}
              isUnderlined
              textProps={{ i18nKey: 'guides.contact-us' }}
            />
          </>
        }
        gridRightMobileEndComponent={
          <>
            <GenericAd className="plc-mb-m" formatId={ADVERT_FORMAT_ID.WideAngle} />
            {lastGuidesCards}
          </>
        }
        illustrationComponent={
          isMobile
            ? featuredMediaMobile && (
                <ClippedImage
                  alt={featuredMediaMobile.alt}
                  caption={featuredMediaMobile.alt}
                  clipStyle={ClippedImageStyle.contentAside}
                  plcSrc={{ src: featuredMediaMobile.url, type: 'external' }}
                />
              )
            : featuredMedia && (
                <ClippedImage
                  alt={featuredMedia.alt}
                  caption={featuredMedia.alt}
                  clipStyle={ClippedImageStyle.contentAside}
                  plcSrc={{ src: featuredMedia.url, type: 'external' }}
                />
              )
        }
        imgAlt={featuredMedia?.alt}
        imgUrl={featuredMedia?.url}
        metas={{
          canonical: canonicalLink,
          metaDescI18n: metaDescription,
          metaTitleI18n: metaTitle || title,
        }}
        postTitleComponent={
          <>
            <Text
              className="plc-mr-s"
              dateTime={formatDateToIso(publishDate)}
              flavour="grey"
              i18nKey="guides.update-date"
              i18nOptions={{ date: formatDateCompact(publishDate) }}
              tag="time"
              variant="h6"
            />
            {articleIsPro && <ProTag className="plc-mt-s plc-mt-up-m-0" variant={ProTagVariant.long} />}
          </>
        }
        tagProps={tagProps}
        withSocialShare={false}
        {...rest}
      >
        <HorizontalSeparator className="plc-mt-m plc-mb-l" />
        <header>
          <Text className={`plc-mb-s plc-mb-up-${breakpoint}-xxs`} tag="div" variant="h5" {...infos} />
          <Text className={`plc-mb-basis plc-mb-up-${breakpoint}-xl`} tag="h2" variant={titleVariant}>
            {title}
          </Text>
        </header>
        <ContentSubtitle className={`plc-mb-basis plc-mb-up-${breakpoint}-l`} subtitle={subtitle} />
        <ContentBody body={body} className={`plc-mb-l plc-mb-up-${breakpoint}-xl`} isPartial={bodyIsPartial} />
        <ContentFooter
          activityCodes={activityCodes}
          className={`plc-mb-l plc-mb-up-${breakpoint}-xl`}
          interestCodes={interestCodes}
          signature={signature}
        />
      </ContentLayout>
      <GuideNavigation
        episodeList={episodeList}
        episodeTitle={title}
        guideTitle={guideTitle}
        style={{
          bottom: '0',
          position: 'sticky',
        }}
      />
    </>
  );
};
